import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { getStoredToken, getStoredUser, hasToolAccess, Sector, isTokenExpired, clearAuthData } from '../services/auth';
import Layout from './Layout';
import { colors } from '../theme/expertiseTheme';

type Props = {
  children: React.ReactNode;
  requiredSector?: Sector;
  adminOnly?: boolean;
};

const AuthGuard: React.FC<Props> = ({ children, requiredSector, adminOnly }) => {
  const token = getStoredToken();
  const user = getStoredUser();

  // Não autenticado - redirecionar para login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Token expirado - limpar dados e redirecionar para login
  if (isTokenExpired()) {
    clearAuthData();
    return <Navigate to="/login" replace />;
  }

  // Verificar se precisa ser admin
  if (adminOnly && !user?.roles?.includes('Admin')) {
    return (
      <Layout title="Acesso Negado">
        <AccessDenied
          message="Esta página é exclusiva para administradores."
          requiredRole="Admin"
        />
      </Layout>
    );
  }

  // Verificar acesso ao setor
  if (requiredSector && !hasToolAccess(user, requiredSector)) {
    return (
      <Layout title="Acesso Negado">
        <AccessDenied
          message={`Você não tem permissão para acessar ferramentas do setor ${requiredSector}.`}
          requiredSector={requiredSector}
        />
      </Layout>
    );
  }

  return <>{children}</>;
};

// Componente de acesso negado
interface AccessDeniedProps {
  message: string;
  requiredRole?: string;
  requiredSector?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ message, requiredRole, requiredSector }) => {
  const user = getStoredUser();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400,
      }}
    >
      <Card sx={{ maxWidth: 480, textAlign: 'center' }}>
        <CardContent sx={{ p: 5 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <LockIcon sx={{ fontSize: 40, color: colors.error }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: colors.dark, mb: 2 }}>
            Acesso Negado
          </Typography>

          <Typography sx={{ color: colors.text.secondary, mb: 3, lineHeight: 1.6 }}>
            {message}
          </Typography>

          <Box
            sx={{
              backgroundColor: colors.light,
              borderRadius: '12px',
              p: 2,
              mb: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
              Seu perfil atual:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>
              Role: {user?.roles?.[0] || 'User'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>
              Setor: {user?.sector || 'Não definido'}
            </Typography>
          </Box>

          {(requiredRole || requiredSector) && (
            <Box
              sx={{
                backgroundColor: 'rgba(220, 38, 38, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(220, 38, 38, 0.2)',
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.error }}>
                Requerido:{' '}
                {requiredRole && <strong>Role {requiredRole}</strong>}
                {requiredRole && requiredSector && ' ou '}
                {requiredSector && <strong>Setor {requiredSector}</strong>}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            href="/home"
            sx={{
              backgroundColor: colors.accent,
              color: colors.dark,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#c9e835',
              },
            }}
          >
            Voltar para Home
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthGuard;
